import {
  add,
  addDays,
  addHours,
  addMinutes,
  addSeconds,
  DateValues,
  differenceInCalendarDays,
  differenceInDays,
  differenceInMinutes,
  DifferenceInMinutesOptions,
  differenceInSeconds,
  Duration,
  endOfDay,
  format,
  fromUnixTime,
  getUnixTime,
  hoursToMinutes,
  Interval,
  isBefore,
  isSameDay as isSameDayDateFns,
  isSameMonth as isSameMonthDateFns,
  isSameYear as isSameYearDateFns,
  isValid,
  isWithinInterval,
  parse,
  parseISO,
  ParseISOOptions,
  ParseOptions,
  set,
  setSeconds,
  startOfDay,
  startOfWeek,
  subDays,
  subHours,
  subMinutes,
} from 'date-fns';

type DateInputDateFormat = `${string}-${string}-${string}`;

// "March 10, 2023"
export const formatDateWithFullMonth = (date: string | Date) => {
  const dateFormat = 'MMMM dd, yyyy';
  return typeof date === 'string' ? format(new Date(date), dateFormat) : format(date, dateFormat);
};

// "10.03.2023"
export const formatDate = (date: string | Date) => {
  const dateFormat = 'dd.MM.yyyy';
  return typeof date === 'string' ? format(new Date(date), dateFormat) : format(date, dateFormat);
};

// "2023-10-03"
export const formatInputDate = (date: string | Date): DateInputDateFormat => {
  const dateFormat = 'yyyy-MM-dd';
  return typeof date === 'string'
    ? (format(new Date(date), dateFormat) as DateInputDateFormat)
    : (format(date, dateFormat) as DateInputDateFormat);
};

// "Fri, March 10"
export const formatDateWithDayAbbreviation = (date: string | Date) => {
  const dateFormat = 'eee, MMMM dd';
  return typeof date === 'string' ? format(new Date(date), dateFormat) : format(date, dateFormat);
};

// "Friday, March 10"
export const formatDateWithFullDay = (date: string | Date) => {
  const dateFormat = 'eeee, MMMM dd';
  return typeof date === 'string' ? format(new Date(date), dateFormat) : format(date, dateFormat);
};

// "21/08 13:45"
export const formatDateToDateWithTime = (date: string | Date) => {
  const dateTimeFormat = 'dd/MM HH:mm';
  return typeof date === 'string'
    ? format(new Date(date), dateTimeFormat)
    : format(date, dateTimeFormat);
};

// "10.03.2023 12:32" separateTimeWithComma=false
// "10.03.2023, 12:32" separateTimeWithComma=true
export const formatDateToFullDateWithTime = (
  date: string | Date,
  separateTimeWithComma?: boolean,
) => {
  const dateFormat = separateTimeWithComma ? 'dd.MM.yyy, HH:mm' : 'dd.MM.yyyy HH:mm';
  return typeof date === 'string' ? format(new Date(date), dateFormat) : format(date, dateFormat);
};

// "08:00"
export const formatDateToTime = (date: InstanceType<typeof Date> | string): string => {
  const timeFormat = 'HH:mm';
  return typeof date === 'string' ? format(new Date(date), timeFormat) : format(date, timeFormat);
};

// 0 .. 86400
export const formatTimeStringToOffsetSeconds = (timeString: string): number => {
  // Create a new Date object for the base date (midnight)
  const baseDate = new Date(2000, 0, 1);

  // Parse the time string
  const parsedDate = parse(timeString, 'HH:mm', baseDate);

  // Calculate seconds since midnight
  return differenceInSeconds(parsedDate, startOfDay(parsedDate));
};

// "12:43"
export const formatOffsetSecondsToTimeString = (offsetSeconds: number): string => {
  // Create a new Date object for the base date (midnight)
  const baseDate = new Date(2000, 0, 1);

  // Add the seconds to the base date
  const resultDate = addSeconds(baseDate, offsetSeconds);

  // Format the result as a time string
  return format(resultDate, 'HH:mm');
};

// 0 .. 86400  => 11:11 (eleven hours and eleven minutes)
export const formatOffsetSecondsToDurationTimeString = (seconds: number): string => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  return format(new Date(0, 0, 0, hours, minutes), 'HH:mm');
};

// "2023-03-13T17:54:00.000Z"
export const convertDateAndTimeToIsoString = ({
  dateString,
  timeString,
}: {
  // YYYY-MM-DD
  dateString: string;
  // HH:MM
  timeString: string;
}) => new Date(`${dateString} ${timeString}`).toISOString();

export const formatDateRange = (
  startDate: InstanceType<typeof Date>,
  endDate: InstanceType<typeof Date>,
) => {
  const isSameDay = isSameDayDateFns(startDate, endDate);
  const isSameMonth = isSameMonthDateFns(startDate, endDate);
  const isSameYear = isSameYearDateFns(startDate, endDate);

  const startDay = format(startDate, 'dd');
  const endDay = format(endDate, 'dd');

  const startMonth = format(startDate, 'MMMM');
  const endMonth = format(endDate, 'MMMM');

  const startYear = format(startDate, 'yyyy');
  const endYear = format(endDate, 'yyyy');

  if (isSameDay && isSameMonth && isSameYear) {
    // March 20, 2023
    return formatDateWithFullMonth(startDate);
  } else if (!isSameDay && isSameMonth && isSameYear) {
    // March 17 - 18, 2023
    return `${startMonth} ${startDay} - ${endDay}, ${startYear}`;
  } else if (!isSameDay && !isSameMonth && isSameYear) {
    // June 30 - July 01, 2022
    return `${startMonth} ${startDay} - ${endMonth} ${endDay}, ${startYear}`;
  } else if (!isSameDay && !isSameMonth && !isSameYear) {
    // December 31, 2022 - January 01, 2023
    return `${startMonth} ${startDay}, ${startYear} - ${endMonth} ${endDay}, ${endYear}`;
  }
};

// 2400 => '00:40'
export const formatSecondsToMinutesSeconds = (totalSeconds: number) => {
  const padWithZero = (value: number): string => String(value).padStart(2, '0');
  // Get minutes and remaining seconds
  const minutes = Math.floor(totalSeconds / 60);
  const remainingSeconds = totalSeconds % 60;
  return `${padWithZero(minutes)}:${padWithZero(remainingSeconds)}`;
};

// 28800 => '08:00'
export const formatSecondsToTime = (seconds: number) =>
  format(addSeconds(new Date(0, 0), seconds), 'HH:mm');

/**
 * Format start and end date to a string containing a date range and a time range
 *
 * @returns
 * A string in the format of either:
 * - `MMMM dd, yyyy, hh:mm - hh:mm` (e.g. `January 01, 2024, 11:25 - 18:35`)
 * - `MMMM dd - dd, yyyy, hh:mm - hh:mm` (e.g. `January 01 - 02, 2024, 11:25 - 18:35`)
 * - `MMMM dd - mmmm dd, yyyy, hh:mm - hh:mm` (e.g. `January 31 - February 01, 2024, 11:25 - 18:35`)
 * - `MMMM dd, yyyy - mmmm dd, yyyy, hh:mm - hh:mm` (e.g. `December 31, 2024 - January 01, 2025, 11:25 - 18:35`)
 */
export const formatDateAndTimeRange = (
  startDate: InstanceType<typeof Date>,
  endDate: InstanceType<typeof Date>,
) => {
  const start = new Date(startDate);
  const end = new Date(endDate);

  const dateRange = formatDateRange(start, end);
  const startTime = formatDateToTime(start);
  const endTime = formatDateToTime(end);

  return `${dateRange}, ${startTime} - ${endTime}`;
};

// 2025-01-27T16:38:13.000+00:00 => 27.01.2025 - 16:38
export const formatISODateStringToDateAndTimeString = (isoDateString: string): string => {
  if (!isoDateString) return '';

  const date = parseISO(isoDateString);
  const formattedDate = format(date, 'dd.MM.yyyy - HH:mm');

  return formattedDate;
};

/**
 * Takes date (format like `2024-01-01`) & time (format like `10:00`)
 * plus the type and service time difference (in minutes, like `10`)
 * to create the iso string for the service time.
 * @param {Object} params - The parameters object
 * @returns An ISO string, like `2024-01-01T20:00:00.000Z`
 */
export const formatServiceTimeToIsoString = ({
  type,
  date,
  time,
  serviceTime,
}: {
  type;
  date: string;
  time: string;
  serviceTime: string | null;
}) => {
  if (!serviceTime) return null;

  const shiftDate = new Date(`${date} ${time}`);
  const minutes = Number(serviceTime);

  return (
    type === 'service_starts_at' ? addMinutes(shiftDate, minutes) : subMinutes(shiftDate, minutes)
  ).toISOString();
};

/**
 * Take the service date ISO string and the respective shift date ISO string and calculates the difference.
 * This is needed to set the pre-selected values for the shift times in edit mode,
 * as the shift time is provided as an ISO string date, but we just need the amount of minutes.
 * @param {Object} params - The parameters object
 * @returns Service time in minutes as a `string`
 */
export const calculateServiceTimeInMinutes = ({
  serviceDate,
  shiftDate,
}: {
  serviceDate: string | null;
  shiftDate: string;
}) => {
  if (!serviceDate) return null;

  return String(Math.abs(differenceInMinutes(parseISO(serviceDate), parseISO(shiftDate))));
};

export const isSameDay = (startsAt: string, endsAt: string) => isSameDayDateFns(startsAt, endsAt);


export const formatUnixCustomStartEndTimeToDate = (
  unixStartDate: string | null,
  unixEndDate: string | null,
) => {
  if (!unixStartDate || !unixEndDate) return;
  const startDate = fromUnixTime(unixStartDate as unknown as number);
  const endDate = fromUnixTime(unixEndDate as unknown as number);
  if (!isValidDate(startDate) || !isValidDate(endDate)) return;
  return { startDate, endDate };
};

export const getUnixTimeDate = (date?: string) => getUnixTime(!date ? new Date() : new Date(date));

export const isValidDate = (date: unknown) => isValid(date);

export const subDaysFromDate = (date: Date, days: number) => subDays(date, days);

export const addDaysToDate = (date: Date, days: number) => addDays(date, days);

export const subHoursFromDate = (date: Date, hours: number) => subHours(date, hours);

export const addHoursToDate = (date: Date, hours: number) => addHours(date, hours);

export const getTotalDurationInMinutes = (hops) => {
  const cumulativeDurationInSeconds = hops
    ?.map((hop) => hop?.duration)
    .reduce((total, duration) => total + duration, 0);
  const cumulativeDurationInMinutes = cumulativeDurationInSeconds / 60;
  return Number.isFinite(cumulativeDurationInMinutes) ? cumulativeDurationInMinutes : 0;
};

export const addMinutesToDate = (date: string | number | Date, amount: number) =>
  addMinutes(date, amount);

export const subMinutesFromDate = (date: string | number | Date, amount: number) =>
  subMinutes(date, amount);

export const addToDate = (date: string | number | Date, duration: Duration) => add(date, duration);

export const setToDate = (date: string | number | Date, values: DateValues) => set(date, values);

export const isDateBefore = (date: string | number | Date, dateToCompare: string | number | Date) =>
  isBefore(date, dateToCompare);

export const isDateWithinInterval = (date: string | number | Date, interval: Interval<Date>) =>
  isWithinInterval(date, interval);

export const setDateSeconds = (date: string | number | Date, seconds: number) =>
  setSeconds(date, seconds);

export const transformHoursToMinutes = (hours: number) => hoursToMinutes(hours);

export const getDifferenceInCalendarDays = (
  dateLeft: string | number | Date,
  dateRight: string | number | Date,
) => differenceInCalendarDays(dateLeft, dateRight);

export const getDifferenceInMinutes = (
  dateLeft: string | number | Date,
  dateRight: string | number | Date,
  options?: DifferenceInMinutesOptions | undefined,
) => differenceInMinutes(dateLeft, dateRight, options);

export const getEndOfDay = (date: string | number | Date) => endOfDay(date);

export const getStartOfDay = (date: string | number | Date) => startOfDay(date);

export const parseInISO = (argument: string, options?: ParseISOOptions | undefined) =>
  parseISO(argument, options);

export const parseDateTimeString = (
  dateStr: string,
  formatStr: string,
  referenceDate: any,
  options?: ParseOptions<any>,
): Date => parse(dateStr, formatStr, referenceDate, options);

export interface WeekDay {
  date: Date;
  isToday: boolean;
  offsetFromToday: number;
}

export const parseDayStringToDays = (dayString: string): WeekDay[] => {
  let date: Date;

  try {
    date = new Date(dayString);
    if (isNaN(date.getTime())) {
      throw new Error();
    }
  } catch {
    date = new Date();
  }

  const firstDayOfWeek = startOfWeek(date, { weekStartsOn: 1 });

  // Calculate the days of the week
  const weekDays: WeekDay[] = [];
  const today = new Date();

  for (let i = 0; i < 7; i++) {
    const day = addDaysToDate(firstDayOfWeek, i);

    const isToday = isSameDayDateFns(day, today);

    // Calculate offset from today using date-fns
    const offsetFromToday = differenceInDays(
      format(day, 'yyyy-MM-dd'),
      format(today, 'yyyy-MM-dd'),
    );

    weekDays.push({
      date: day,
      isToday: isToday,
      offsetFromToday: offsetFromToday, // Add the new property
    });
  }

  return weekDays;
};
